import { getAllReceiptsFees } from 'lib/api/accounting';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import GlobalSearcher, {
  Filter,
} from '../../../../../../lib/common/GlobalSearcher';
import CenteredCircularProgress from '../../../../../../lib/designSystem/CircularProgress';
import HeaderWithActions, {
  CustomButtonProps,
} from '../../../../../../lib/designSystem/HeaderWithActions';
import TableWithPagination from '../../../../../../lib/designSystem/TableWithPagination';
import { showAlert } from '../../../../../../lib/functions/utils';
import { ReceiptFeeDocument } from '../../types';
import AssignmentDialog from '../PurchansesAndSales/components/AssignmentDialog';

const ReceiptFees: React.FC = () => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [receiptFees, setReceiptFees] = useState<ReceiptFeeDocument[]>([]);
  const [searchFolio, setSearchFolio] = useState('');
  const [startDate, setStartDate] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<string | null>(null);
  const [hasCancelDate, setHasCancelDate] = useState<string>('false');
  const [identifier, setIdentifier] = useState<string | null>(null);
  const [name, setName] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [assignationModuleOpen, setAssignationModuleOpen] = useState(false);
  const [selectedDocuments, setSelectedDocuments] = useState<any[]>([]);

  const PAGE_SIZE = 10;
  const DEFAULT_PAGE = 1;
  const [pagination, setPagination] = useState({
    page: DEFAULT_PAGE,
    pageSize: PAGE_SIZE,
    total: 0,
    totalPages: 0,
  });

  const headers: { key: string; label: string; type?: string }[] = [
    {
      key: 'centralizationStatus',
      label: 'Estado centralización',
      type: 'status-chip',
    },
    { key: 'folio', label: 'Folio' },
    { key: 'businessName', label: 'Proveedor' },
    { key: 'stakeholderName', label: 'Deudor' },
    { key: 'businessIdentifier', label: 'Rut Proveedor' },
    { key: 'stakeholderIdentifier', label: 'Rut Deudor' },
    { key: 'amount', label: 'Monto', type: 'money' },
    { key: 'issuedDate', label: 'Emisión', type: 'date' },
    { key: 'receiptDate', label: 'Recepción', type: 'date' },
    { key: 'confirmDate', label: 'Acuse', type: 'date' },
    { key: 'cancelDate', label: 'Cancelación', type: 'date' },
    { key: 'exentAmount', label: 'Monto exento', type: 'money' },
    { key: 'retentionAmount', label: 'Monto retención', type: 'money' },
    { key: 'netAmount', label: 'Monto neto', type: 'money' },
    { key: 'ivaAmount', label: 'Monto IVA', type: 'money' },
    {
      key: 'ivaNotRecoverableAmount',
      label: 'Monto IVA no recuperable',
      type: 'money',
    },
    {
      key: 'ivaRecoverableAmount',
      label: 'Monto IVA recuperable',
      type: 'money',
    },
    { key: 'financialNode', label: 'Cuenta' },
    { key: 'financialNodeAccountCode', label: 'Código de cuenta' },
    { key: 'costCenterNode', label: 'Centro de costo' },
  ];

  const generateQuery = ({
    page,
    pageSize,
  }: {
    page: number;
    pageSize: number;
  }) => {
    let query = '';
    if (startDate) query += `startDate=${startDate}&`;
    if (endDate) query += `endDate=${endDate}&`;
    if (name) query += `name=${name}&`;
    if (searchFolio) query += `folio=${searchFolio}&`;
    if (hasCancelDate) query += `hasCancelDate=${hasCancelDate}&`;
    if (identifier) query += `identifier=${identifier}&`;
    if (page) query += `page=${page}&`;
    if (pageSize) query += `pageSize=${pageSize}&`;
    query += `type=purchase`;
    return query;
  };
  const handleAssignationModule = () => {
    // Filtrar los documentos seleccionados
    const selectedDocs = receiptFees.filter((doc) =>
      selectedIds.includes(doc.id),
    );
    setSelectedDocuments(selectedDocs);
    setAssignationModuleOpen(true);
  };
  const fetchReceiptFees = async ({
    page,
    pageSize,
  }: {
    page: number;
    pageSize: number;
  }) => {
    setLoading(true);
    try {
      const query = generateQuery({ page, pageSize });
      const response = await getAllReceiptsFees(dispatch, query);
      setReceiptFees(response.data);
      setPagination(response.pagination);
      setLoading(false);
      return response;
    } catch (error) {
      console.error(error);
      showAlert(
        dispatch,
        'error',
        'Error al obtener los recibos de honorarios',
      );
    }
  };

  const setUp = async () => {
    await fetchReceiptFees({ page: 1, pageSize: 10 });
  };

  useEffect(() => {
    void setUp();
  }, []);

  const filters: Filter[] = [
    {
      type: 'text',
      label: 'Proveedor',
      value: identifier,
      setter: setIdentifier,
    },
    {
      type: 'autocomplete',
      label: 'Nombre proveedor',
      key: 'businessName',
      value: name,
      setter: setName,
    },
    {
      type: 'text',
      label: 'Folio',
      value: searchFolio,
      setter: setSearchFolio,
    },
    {
      type: 'date',
      label: 'Fecha emisión',
      dateKey: 'startDate',
      value: startDate,
      setter: setStartDate,
    },
    {
      type: 'date',
      label: 'Fecha emisión',
      dateKey: 'endDate',
      value: endDate,
      setter: setEndDate,
    },
    {
      type: 'select',
      label: 'Vigencia',
      value: hasCancelDate,
      setter: setHasCancelDate,
      options: [
        { label: 'Todas', value: 'all' },
        { label: 'Vigente', value: 'false' },
        { label: 'Anulada', value: 'true' },
      ],
    },
  ];

  const cantSelectRow = (row: ReceiptFeeDocument) => {
    return row.cancelDate !== null;
  };

  // Helper function to filter out nulled receipts from selected IDs
  const filterNulledReceipts = (ids: number[]): number[] => {
    return ids.filter((id) => {
      const receipt = receiptFees.find((r) => r.id === id);
      return receipt && receipt.cancelDate === null;
    });
  };

  // Wrapper for setSelectedIds that filters out nulled receipts
  const setSelectedIdsFiltered = (ids: number[]) => {
    setSelectedIds(filterNulledReceipts(ids));
  };

  // Clean up any nulled receipts that might be selected when data changes
  useEffect(() => {
    if (selectedIds.length > 0) {
      const filteredIds = filterNulledReceipts(selectedIds);
      if (filteredIds.length !== selectedIds.length) {
        setSelectedIds(filteredIds);
      }
    }
  }, [receiptFees]);

  const buttons: CustomButtonProps[] = [
    {
      name: 'Buscar',
      color: 'secondary',
      action: () => {},
      customButton: (
        <GlobalSearcher
          filters={filters}
          triggerSearch={() => fetchReceiptFees({ page: 1, pageSize: 10 })}
        />
      ),
    },
    {
      name: 'Asignar',
      color: 'primary',
      action: handleAssignationModule,
      disabled: selectedIds.length === 0,
    },
  ];

  if (loading) return <CenteredCircularProgress />;

  return (
    <div>
      <HeaderWithActions
        title={`Acá encontrarás todas las boletas de honorarios`}
        variant="body1"
        buttons={buttons}
      />
      <TableWithPagination
        data={receiptFees}
        dataHeaders={headers}
        selected={selectedIds}
        setSelected={setSelectedIdsFiltered}
        canSelectAll={true}
        cantSelectRow={cantSelectRow}
        finalSumRow={true}
        exportToFile="xlsx"
        pagination={pagination}
        setPagination={setPagination}
        setData={setReceiptFees}
        searchData={fetchReceiptFees}
        setLoading={setLoading}
      />

      {/* Receipt Fee Dialog */}
      {assignationModuleOpen && selectedDocuments && (
        <AssignmentDialog
          open={assignationModuleOpen}
          onClose={() => setAssignationModuleOpen(false)}
          documents={selectedDocuments}
          onAssignmentComplete={() => {
            void setUp();
            setSelectedIds([]);
          }}
        />
      )}
    </div>
  );
};

export default ReceiptFees;
